import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, Save, RotateCcw } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { rollePermissionsApi } from '../lib/api';
import { isRolePermissionEditable } from '../lib/rolePermissionMatrix';

const ROLLE_LABELS = {
  DEVELOPER: 'Developer',
  WACHLEITUNG: 'Wachleitung',
  WACHLEITUNG_VERTRETUNG: 'Wachleitung Vertr.',
  WACHABTEILUNGSLEITUNG: 'Wachabt.-Leitung',
  ADMIN: 'Einteiler (Admin)',
  MITARBEITER: 'Mitarbeiter',
};

export default function RollePermissionsPage() {
  const [data, setData] = useState(null);   // { permissions, rollen, matrix }
  const [draft, setDraft] = useState(null); // Kopie der Matrix zum Bearbeiten
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await rollePermissionsApi.getMatrix();
      setData(res.data);
      setDraft(structuredClone(res.data.matrix));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte Matrix nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isDirty = useMemo(() => {
    if (!data || !draft) return false;
    return JSON.stringify(data.matrix) !== JSON.stringify(draft);
  }, [data, draft]);

  const toggle = (rolle, key) => {
    setDraft((prev) => ({
      ...prev,
      [rolle]: { ...prev[rolle], [key]: !prev[rolle][key] },
    }));
  };

  const save = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      // Nur geänderte Einträge senden — kompakter Payload
      const entries = [];
      Object.entries(draft).forEach(([rolle, perms]) => {
        Object.entries(perms).forEach(([key, val]) => {
          const orig = data.matrix?.[rolle]?.[key];
          if (orig !== val) entries.push({ rolle, permission_key: key, erlaubt: val });
        });
      });
      const res = await rollePermissionsApi.updateMatrix(entries);
      setData(res.data);
      setDraft(structuredClone(res.data.matrix));
      toast.success('Rechte gespeichert — sofort wirksam');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (data) setDraft(structuredClone(data.matrix));
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="py-12 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-4 p-4">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Rechteverwaltung</CardTitle>
              <CardDescription>
                Konfiguriere pro Rolle welche Aktionen erlaubt sind. Der DEVELOPER hat immer alle Rechte
                (kann nicht deaktiviert werden). Änderungen werden nach dem Speichern sofort wirksam.
              </CardDescription>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" onClick={reset} disabled={!isDirty || saving} data-testid="permissions-reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Zurücksetzen
              </Button>
              <Button onClick={save} disabled={!isDirty || saving} data-testid="permissions-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                Speichern
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left font-semibold py-2 pr-3 sticky left-0 bg-card">Berechtigung</th>
                  {data.rollen.map((r) => (
                    <th key={r} className="text-center font-semibold py-2 px-2 min-w-[110px]">
                      {ROLLE_LABELS[r] || r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.permissions.map((p) => (
                  <tr key={p.key} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-3 sticky left-0 bg-card">
                      <div className="font-medium">{p.label}</div>
                    </td>
                    {data.rollen.map((r) => {
                      const editable = isRolePermissionEditable(r);
                      const val = !!draft?.[r]?.[p.key];
                      return (
                        <td key={r} className="text-center py-2 px-2">
                          <Checkbox
                            checked={val}
                            disabled={!editable}
                            onCheckedChange={() => editable && toggle(r, p.key)}
                            data-testid={`permission-${r}-${p.key.replace(/\./g, '_')}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
